import React, { useState, useEffect } from 'react';
import Paper from '@material-ui/core/Paper';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import { surgeryService } from '../../../services/surgeryService';

const THEATRE_TYPE_LABELS = {
  general: 'Major OR',
  minor: 'Minor OR',
  cardiac: 'Cardiac OR',
  neuro: 'Neuro OR',
};

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: 32,
  },
  card: {
    padding: 16,
    cursor: 'pointer',
    border: '2px solid #e0e0e0',
    borderRadius: 8,
    transition: 'border-color 0.2s, box-shadow 0.2s',
    marginBottom: 10,
    '&:hover': {
      borderColor: '#90CAF9',
    },
  },
  cardSelected: {
    borderColor: '#2196F3',
    boxShadow: '0 0 0 1px #2196F3',
  },
  cardDisabled: {
    opacity: 0.5,
    cursor: 'default',
    '&:hover': {
      borderColor: '#e0e0e0',
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  theatreName: {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: '#333',
  },
  theatreType: {
    fontSize: '0.8rem',
    color: '#666',
    marginLeft: 8,
    backgroundColor: '#f0f0f0',
    padding: '2px 8px',
    borderRadius: 4,
  },
  location: {
    fontSize: '0.8rem',
    color: '#888',
    marginTop: 2,
  },
  slotsText: {
    fontSize: '0.82rem',
    fontWeight: 500,
    marginTop: 4,
  },
  slotsAvailable: {
    color: '#4CAF50',
  },
  fullyBooked: {
    color: '#F44336',
  },
  slotsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
    paddingTop: 12,
    borderTop: '1px solid #eee',
  },
  slotChip: {
    fontWeight: 500,
    fontSize: '0.78rem',
  },
  formControlLabel: {
    margin: 0,
    alignItems: 'flex-start',
    width: '100%',
  },
  radioRoot: {
    paddingTop: 2,
  },
  labelContent: {
    flex: 1,
  },
}));

const TheatrePicker = ({
  value = null,
  onChange,
  date,
  disabled = false,
}) => {
  const classes = useStyles();
  const [theatres, setTheatres] = useState([]);
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => {
    setLoading(true);
    setSelectedSlot(null);

    Promise.all([
      surgeryService.listTheatres(),
      surgeryService.theatreCalendar({ date: date || new Date().toISOString().slice(0, 10) }),
    ])
      .then(([theatreRes, calendarRes]) => {
        const theatreList = theatreRes.data?.theatres || theatreRes.data || [];
        const slots = calendarRes.data?.slots || [];

        // Build availability map: theatre name -> available slots
        const availMap = {};
        theatreList.forEach((t) => {
          availMap[t.id] = [];
        });

        slots.forEach((slot) => {
          const theatre = theatreList.find((t) => t.name === slot.theatre);
          if (theatre && slot.status === 'available') {
            if (!availMap[theatre.id]) availMap[theatre.id] = [];
            availMap[theatre.id].push(slot.time);
          }
        });

        setTheatres(theatreList);
        setAvailability(availMap);
      })
      .catch(() => {
        setTheatres([]);
        setAvailability({});
      })
      .finally(() => {
        setLoading(false);
      });
  }, [date]);

  const handleSelect = (theatre) => {
    if (disabled) return;
    setSelectedSlot(null);
    if (onChange) onChange(theatre);
  };

  const handleSlotClick = (slot) => {
    setSelectedSlot(slot);
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={28} />
      </div>
    );
  }

  const selectedId = value ? value.id : null;

  return (
    <div className={classes.root}>
      <RadioGroup value={selectedId || ''}>
        {theatres.map((theatre) => {
          const slots = availability[theatre.id] || [];
          const slotCount = slots.length;
          const isSelected = selectedId === theatre.id;
          const fullyBooked = slotCount === 0;

          return (
            <Paper
              key={theatre.id}
              className={`${classes.card} ${
                isSelected ? classes.cardSelected : ''
              } ${disabled ? classes.cardDisabled : ''}`}
              elevation={isSelected ? 2 : 0}
              onClick={() => handleSelect(theatre)}
            >
              <FormControlLabel
                className={classes.formControlLabel}
                value={theatre.id}
                control={
                  <Radio
                    color="primary"
                    checked={isSelected}
                    disabled={disabled}
                    className={classes.radioRoot}
                  />
                }
                label={
                  <div className={classes.labelContent}>
                    <div className={classes.cardHeader}>
                      <Typography className={classes.theatreName}>
                        {theatre.name}
                      </Typography>
                      <span className={classes.theatreType}>
                        {THEATRE_TYPE_LABELS[theatre.type] || theatre.type}
                      </span>
                    </div>
                    <Typography className={classes.location}>
                      {theatre.location}
                    </Typography>
                    <Typography
                      className={`${classes.slotsText} ${
                        fullyBooked
                          ? classes.fullyBooked
                          : classes.slotsAvailable
                      }`}
                    >
                      {fullyBooked
                        ? 'Fully booked'
                        : `${slotCount} slot${slotCount !== 1 ? 's' : ''} available`}
                    </Typography>

                    {isSelected && slotCount > 0 && (
                      <Box className={classes.slotsContainer}>
                        {slots.map((slot) => (
                          <Chip
                            key={slot}
                            label={slot}
                            className={classes.slotChip}
                            size="small"
                            color={selectedSlot === slot ? 'primary' : 'default'}
                            variant={
                              selectedSlot === slot ? 'default' : 'outlined'
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSlotClick(slot);
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </div>
                }
              />
            </Paper>
          );
        })}
      </RadioGroup>
    </div>
  );
};

export default TheatrePicker;
